import type { User } from "firebase/auth";
import { authed } from "./referral";

export type BillingPlan = "hours" | "monthly";
export type BillingMode = "live" | "test";

export interface BillingStatus {
  isAdmin: boolean;
  mode: BillingMode;
  purchasedHours: number;
  hasBillingAccount: boolean;
  /** At least one custom quote has been paid. */
  licensed?: boolean;
  subscription: { active: boolean; status: string | null; cancelAtPeriodEnd: boolean; currentPeriodEnd: string | null };
}

export interface CheckoutStart {
  clientSecret: string;
  publishableKey: string;
  mode: BillingMode;
}

/** Shown on the plan cards; Stripe's checkout shows the real amount, so keep these in step with the prices in Stripe. */
export const PLANS: Record<BillingPlan, { title: string; price: string; detail: string }> = {
  hours: { title: "Pay as you go", price: "$100", detail: "Two hours of live captioning. Buy more any time." },
  monthly: { title: "Monthly plan", price: "$400 / month", detail: "Unlimited use while subscribed. Cancel any time." },
};

/** Where people who subscribed through the old shared payment links manage that subscription (they sign in with their email). */
export const LEGACY_PORTAL_URL = "https://pay.conferencecaptioning.com/p/login/00g9CkbSS9mK49ObII";

async function json<T>(res: Response, what: string): Promise<T> {
  if (!res.ok) throw new Error(`${what} ${res.status}`);
  return res.json();
}

export async function fetchBillingStatus(user: User): Promise<BillingStatus> {
  return json(await authed(user, "/payments/status"), "payments/status");
}

export async function startCheckout(user: User, plan: BillingPlan): Promise<CheckoutStart> {
  return json(await authed(user, "/payments/createcheckoutsession", { method: "POST", body: JSON.stringify({ plan }) }), "payments/createcheckoutsession");
}

export async function openBillingPortal(user: User): Promise<string> {
  const { url } = await json<{ url: string }>(await authed(user, "/payments/portalsession", { method: "POST" }), "payments/portalsession");
  return url;
}

/** Admins only (the API answers 403 for anyone else). */
export async function setBillingMode(user: User, mode: BillingMode): Promise<BillingMode> {
  const res = await json<{ mode: BillingMode }>(await authed(user, "/payments/setmode", { method: "POST", body: JSON.stringify({ mode }) }), "payments/setmode");
  return res.mode;
}

const BASELINE_KEY = "cc_billing_baseline";

/** Remembered across the redirect Stripe's checkout ends with, so the page can tell when the webhook has caught up. */
export function rememberBaseline(status: BillingStatus): void {
  try {
    sessionStorage.setItem(BASELINE_KEY, JSON.stringify({ hours: status.purchasedHours, active: status.subscription.active }));
  } catch {
    /* storage blocked: the page then just shows the status as it is */
  }
}

export function takeBaseline(): { hours: number; active: boolean } | null {
  try {
    const raw = sessionStorage.getItem(BASELINE_KEY);
    sessionStorage.removeItem(BASELINE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** True once the webhook has recorded something new since the baseline was taken. */
export function paymentRecorded(status: BillingStatus, baseline: { hours: number; active: boolean }): boolean {
  return status.purchasedHours > baseline.hours || (status.subscription.active && !baseline.active);
}

/** Polls the status until the payment shows up (webhooks arrive a moment after the redirect) or the time runs out. */
export async function waitForPayment(
  user: User,
  baseline: { hours: number; active: boolean },
  { intervalMs = 2000, timeoutMs = 40000 }: { intervalMs?: number; timeoutMs?: number } = {}
): Promise<BillingStatus | null> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      const status = await fetchBillingStatus(user);
      if (paymentRecorded(status, baseline)) return status;
    } catch {
      /* try again until the deadline */
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
}
